import { createRef, Component, Fragment } from "react";
import { array, bool, func, node, object, shape, string } from "prop-types";
import styled from "styled-components/macro";
import { clearFix, rem } from "polished";
import { matchPath } from "react-router-dom";

import { routes } from "../../../constants";
import { colors } from "../../../styles/Styles.variables";

import Chat from "../../chat";
import StylesDesktop from "../../../styles/Styles.desktop";
import PageDesktopFooter from "./Page.desktop.footer";
import PageDesktopHeader from "./Page.desktop.header";
import CompareDrawer from "../../compare-drawer";

// It would be better if this was obtained from the DOM,
// but setting the ref here and passing to component
// that is lazy loaded inside the child component
// adds too much complexity and the drawer should have
// a fixed height
const compareDrawerHeight = 175;
const selectFilesDrawerHeight = 213;

// List of pages that need to override the container background color
const containerBgColor = [
  routes.campaignPage.path,
  routes.landingPage.path,
  routes.results.path,
  routes.specials.path,
];
/**
 * Page wrapper for desktops and tablets
 */
class PageDesktop extends Component {
  constructor(props) {
    super(props);

    this.getInnerBg = this.getInnerBg.bind(this);
    this.getObserver = this.getObserver.bind(this);
    this.skipNavigation = this.skipNavigation.bind(this);

    this.content = createRef();
    this.footer = createRef();

    this.state = {
      innerBg: colors.gray100,
      footerOffset: 0,
    };
  }

  /**
   * Get inner page background color
   * @public
   */
  getInnerBg() {
    const { pathname } = this.props?.location || {};

    const innerBg = matchPath(pathname, {
      path: containerBgColor,
      exact: false,
    })
      ? colors.gray200
      : colors.gray100;

    this.setState({ innerBg });
  }

  /**
   * Get intersection observer to determine if footer is on-screen
   * @public
   */
  getObserver() {
    if (!window || !("IntersectionObserver" in window)) {
      return null;
    }

    const threshold = Array.from(Array(100).keys()).map(n => n / 100);

    return new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          let footerOffset = 0;

          if (entry.isIntersecting) {
            footerOffset = Math.floor(
              window.innerHeight - entry.boundingClientRect.top - 2
            );
          }

          if (footerOffset !== this.state.footerOffset) {
            this.setState({ footerOffset: footerOffset });
          }
        });
      },
      {
        threshold: threshold,
      }
    );
  }

  /**
   * Skip navigation for screen readers
   * @param {event} e
   * @public
   */
  skipNavigation(e) {
    const { content } = this;

    if (!!content.current) {
      content.current.focus();
    } else {
      e.target.blur();
    }
  }

  componentDidMount() {
    this.getInnerBg();
    this.observer = this.getObserver();
    if (this.observer && !!this.footer.current) {
      this.observer.observe(this.footer.current);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps?.location?.pathname !== this.props?.location?.pathname) {
      this.getInnerBg();
    }
  }

  componentWillUnmount() {
    if (!!this.observer) {
      this.observer = this.observer.disconnect();
    }
  }

  render() {
    const { content, footer, skipNavigation } = this;
    const {
      authenticated,
      children,
      hasCompared,
      hasSelectedFiles,
      location,
      logout,
      settingsLinks,
      signupFooterShow,
      targetDevice,
      trackEvent,
      trackOutboundLink,
      user,
    } = this.props;
    const { footerOffset, innerBg } = this.state;
    const isCompare = location.pathname === routes.compare.path;

    return (
      <Fragment>
        <Wrapper>
          <PageDesktopHeader
            authenticated={authenticated}
            location={location}
            logout={logout}
            settingsLinks={settingsLinks}
            skip={skipNavigation}
            trackOutboundLink={trackOutboundLink}
            user={user}
          />
          <Main ref={content} tabIndex="-1">
            <Inner
              currentPath={location.pathname}
              hasCompared={hasCompared}
              hasSelectedFiles={hasSelectedFiles}
              innerBg={innerBg}
            >
              {children}
            </Inner>
            <CompareDrawer trackEvent={trackEvent} />
          </Main>
          <PageDesktopFooter
            authenticated={authenticated}
            location={location}
            ref={footer}
            settingsLinks={settingsLinks}
            signupFooterShow={signupFooterShow}
            trackEvent={trackEvent}
            trackOutboundLink={trackOutboundLink}
          />
          <Chat
            compareDrawerHeight={compareDrawerHeight}
            footerOffset={footerOffset}
            hasCompared={hasCompared}
            hasSelectedFiles={hasSelectedFiles}
            isCompare={isCompare}
            targetDevice={targetDevice}
          />
        </Wrapper>
        <StylesDesktop />
      </Fragment>
    );
  }
}

PageDesktop.propTypes = {
  authenticated: bool,
  children: node.isRequired,
  location: object,
  logout: func.isRequired,
  hasCompared: bool,
  hasSelectedFiles: bool,
  settingsLinks: array,
  signupFooterShow: bool,
  targetDevice: string,
  trackEvent: func,
  trackOutboundLink: func,
  user: shape({
    email: string,
    name: string,
  }),
};

PageDesktop.defaultProps = {
  hasCompared: false,
  hasSelectedFiles: false,
  targetDevice: "desktop",
};

const Wrapper = styled.div`
  display: block;
  display: grid;
  min-height: 100vh;
  grid-template-columns: 100%;
  grid-template-rows: auto 1fr auto;
`;

const Main = styled.main`
  position: relative;

  &:focus {
    outline: none;
  }
`;

const Inner = styled.div`
  ${clearFix()}
  background-color: ${({ innerBg }) => innerBg};
  display: block;
  height: ${getInnerHeight};
  outline: none;
  margin: 0 auto;
  max-width: ${({ currentPath }) =>
    currentPath === routes.results.path ? rem("1392px") : rem("1440px")};
  width: 100%;
`;

function getInnerHeight({ currentPath, hasCompared, hasSelectedFiles }) {
  if (hasSelectedFiles) {
    return `calc(100% - ${rem(selectFilesDrawerHeight + "px")})`;
  }

  if (hasCompared && currentPath !== routes.compare.path) {
    return `calc(100% - ${rem(compareDrawerHeight + "px")})`;
  }

  return "100%";
}

export default PageDesktop;
